import { useSyncExternalStore } from "react";
import { routing } from "@/i18n/routing";

// Internal pathname of the route a page transition is navigating to, set at
// leave-start (before the route commits) so scenes can begin their per-route
// animation together with the page transition instead of after it. Null when
// no transition is in flight — consumers fall back to usePathname().
let upcoming: string | null = null;
const listeners = new Set<() => void>();

// Map a raw anchor href (localized + locale-prefixed, e.g. "/de/kontakt/")
// to the internal pathname usePathname() would return (e.g. "/contact").
const toInternalPathname = (href: string): string | null => {
  const path = href.split(/[?#]/)[0];
  if (!path) return null;
  const stripped =
    path
      .replace(new RegExp(`^/(${routing.locales.join("|")})(?=/|$)`), "")
      .replace(/\/+$/, "") || "/";
  for (const [internal, localized] of Object.entries(routing.pathnames)) {
    if (
      typeof localized === "string"
        ? localized === stripped
        : (Object.values(localized) as string[]).includes(stripped)
    ) {
      return internal;
    }
  }
  return stripped;
};

export const setUpcomingPathname = (href: string | null) => {
  const next = href === null ? null : toInternalPathname(href);
  if (next === upcoming) return;
  upcoming = next;
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useUpcomingPathname = () =>
  useSyncExternalStore(
    subscribe,
    () => upcoming,
    () => null
  );
